import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import { Award, Plus, Edit, Trash2, AlertCircle, Save, X, ExternalLink, RefreshCw, CheckCircle2 } from 'lucide-react'

interface ResultLink {
  id: string
  title: string
  url: string
  description: string | null
  display_order: number | null
  is_active: number | null
  created_at: string | null
  updated_at: string | null
}

export function ResultLinksManager() {
  const [links, setLinks] = useState<ResultLink[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [statusMessage, setStatusMessage] = useState('')
  const [statusType, setStatusType] = useState<'error' | 'success' | ''>('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingLink, setEditingLink] = useState<ResultLink | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    description: '',
    is_active: true
  })

  const fetchLinks = async () => {
    try {
      const { data, error } = await supabase
        .from('result_links')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setLinks(data || [])
    } catch (err) {
      setError('Failed to fetch result links')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLinks()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setStatusMessage('')
    setStatusType('')

    try {
      if (editingLink) {
        // Update existing
        const { error } = await supabase
          .from('result_links')
          .update({
            title: formData.title.trim(),
            url: formData.url.trim(),
            description: formData.description.trim() || null,
            is_active: formData.is_active ? 1 : 0,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingLink.id)

        if (error) throw error
        setStatusType('success')
        setStatusMessage('Result link updated successfully.')
      } else {
        // Create new
        const { error } = await supabase
          .from('result_links')
          .insert({
            title: formData.title.trim(),
            url: formData.url.trim(),
            description: formData.description.trim() || null,
            is_active: formData.is_active ? 1 : 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })

        if (error) throw error
        setStatusType('success')
        setStatusMessage('Result link added successfully.')
      }

      await fetchLinks()
      handleCloseDialog()
    } catch (err: any) {
      setStatusType('error')
      setStatusMessage(err.message || 'Failed to save result link.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('result_links')
        .delete()
        .eq('id', id)

      if (error) throw error
      setStatusType('success')
      setStatusMessage('Result link deleted successfully.')
      await fetchLinks()
    } catch (err: any) {
      setStatusType('error')
      setStatusMessage(err.message || 'Failed to delete result link.')
    }
  }

  const handleEdit = (link: ResultLink) => {
    setEditingLink(link)
    setFormData({
      title: link.title,
      url: link.url,
      description: link.description || '',
      is_active: link.is_active === 1
    })
    setIsDialogOpen(true)
  }

  const handleAddNew = () => {
    setEditingLink(null)
    setFormData({
      title: '',
      url: '',
      description: '',
      is_active: true
    })
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingLink(null)
    setFormData({
      title: '',
      url: '',
      description: '',
      is_active: true
    })
  }

  const handleToggleActive = async (link: ResultLink) => {
    try {
      const { error } = await supabase
        .from('result_links')
        .update({
          is_active: link.is_active === 1 ? 0 : 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', link.id)

      if (error) throw error
      await fetchLinks()
    } catch (err: any) {
      setError(err.message || 'Failed to update link status')
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">
            Result Links Management
          </h2>
          <p className="text-slate-600 text-lg">
            Manage result links for the student section
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={fetchLinks}
            disabled={loading}
            className="w-full sm:w-auto hover:bg-purple-50 hover:border-purple-200 hover:text-purple-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleAddNew}
            className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Result Link
          </Button>
        </div>
      </div>

      {statusMessage && (
        <Alert
          className={
            statusType === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : statusType === 'error'
                ? undefined
                : undefined
          }
          variant={statusType === 'error' ? 'destructive' : 'default'}
        >
          {statusType === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{statusMessage}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5" />
            Result Links ({links.length})
          </CardTitle>
          <CardDescription>
            Manage result links displayed in the student section
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <RefreshCw className="h-6 w-6 animate-spin mr-3" />
              Loading result links...
            </div>
          ) : links.length === 0 ? (
            <div className="text-center py-10">
              <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No result links added yet.</p>
              <Button onClick={handleAddNew} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add First Link
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.map((link) => (
                  <TableRow key={link.id}>
                    <TableCell className="font-medium">{link.title}</TableCell>
                    <TableCell>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-1 text-sm"
                      >
                        {link.url.length > 40 ? `${link.url.substring(0, 40)}...` : link.url}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                      {link.description || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={link.is_active === 1}
                          onCheckedChange={() => handleToggleActive(link)}
                        />
                        <Badge variant={link.is_active === 1 ? 'default' : 'secondary'}>
                          {link.is_active === 1 ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(link)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete result link?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently remove this result link from the system.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600"
                                onClick={() => handleDelete(link.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingLink ? 'Edit Result Link' : 'Add New Result Link'}
            </DialogTitle>
            <DialogDescription>
              {editingLink ? 'Update result link information' : 'Add a new result link for students'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., 10th Board Results 2024"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">URL *</Label>
              <Input
                id="url"
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com/results"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the result link"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="is_active">Status</Label>
              <div className="flex items-center gap-2 pt-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <span className="text-sm text-muted-foreground">
                  {formData.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleCloseDialog}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

